import React, { useState, useEffect } from "react";
import { useConsent } from "../context/ConsentContext";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { track } from "../analytics/tracker";
import { EV } from "../analytics/events";

const CookieBanner = () => {
  const {
    isBannerVisible,
    isSettingsOpen,
    acceptAll,
    rejectAll,
    saveChoices,
    closeSettings,
    getConsent,
  } = useConsent();

  const { t } = useTranslation();

  // init checkboxa iz storage-a (kad otvoriš settings iz footera)
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = getConsent();
    setAnalytics(!!c.consent_analytics);
    setMarketing(!!c.consent_marketing);
  }, [isSettingsOpen, getConsent]);

  if (!isBannerVisible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    track(EV.CONSENT_UPDATE, {
      consent_analytics: true,
      consent_marketing: true,
      metadata: { action: "accept_all" },
    });
  };

  const handleRejectAll = () => {
    rejectAll();
    track(EV.CONSENT_UPDATE, {
      consent_analytics: false,
      consent_marketing: false,
      metadata: { action: "reject_all" },
    });
  };

  const handleSave = () => {
    saveChoices({ analytics, marketing });
    track(EV.CONSENT_UPDATE, {
      consent_analytics: analytics,
      consent_marketing: marketing,
      metadata: { action: "save_choices" },
    });
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background-light dark:bg-background-dark text-sm text-black dark:text-white border-t border-primary-light dark:border-primary-dark shadow-md z-[9999]">
      <div className="max-w-4xl mx-auto px-4 py-4 flex flex-col gap-3 text-center">
        <div>
          {t("cookie.message")}{" "}
          <Link to="/cookies-policy" className="underline ml-1 hover:opacity-80">
            {t("cookie.learn_more")}
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button
            onClick={handleRejectAll}
            className="px-4 py-2 rounded border border-gray-300 dark:border-neutral-700 hover:opacity-90 transition"
          >
            {t("cookie.decline")}
          </button>

          <button
            onClick={handleAcceptAll}
            className="px-5 py-2 rounded bg-primary-light dark:bg-primary-dark text-white dark:text-black font-semibold hover:opacity-90 transition"
          >
            {t("cookie.accept")}
          </button>

          <button
            onClick={() => {
              // samo otvori settings UI (banner već vidiš)
              // ti ovo već radiš preko context openSettings iz footera;
              // ovde samo prikaži settings deo:
              // najbolje: koristi openSettings() ali ga nisi destructurovao ovde.
              // Alternativa: nemoj dugme "Customize" ovde ako ga rešavaš iz footera.
            }}
            className="underline hover:opacity-80"
          >
            {t("cookie.settings", "Customize")}
          </button>
        </div>

        {isSettingsOpen && (
          <div className="mt-2 mx-auto w-full max-w-xl text-left rounded-xl border border-gray-200 dark:border-neutral-800 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold">{t("cookie.settings_title", "Privacy settings")}</div>
                <div className="text-xs opacity-80 mt-1">
                  {t("cookie.settings_hint", "Essential cookies are always on.")}
                </div>
              </div>

              <button
                onClick={() => {
                  setAnalytics(true);
                  setMarketing(true);
                }}
                className="text-xs px-3 py-1.5 rounded bg-primary-light/10 dark:bg-primary-dark/20 hover:opacity-90"
              >
                {t("cookie.enable_all", "Enable all")}
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium">{t("cookie.analytics", "Analytics")}</div>
                  <div className="text-xs opacity-75">{t("cookie.analytics_desc", "Helps us improve the site.")}</div>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="h-5 w-5"
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium">{t("cookie.marketing", "Marketing")}</div>
                  <div className="text-xs opacity-75">{t("cookie.marketing_desc", "Personalized ads & retargeting.")}</div>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="h-5 w-5"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-3 justify-end">
              <button
                onClick={closeSettings}
                className="px-4 py-2 rounded border border-gray-300 dark:border-neutral-700 hover:opacity-90"
              >
                {t("cookie.cancel", "Cancel")}
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2 rounded bg-slate-900 dark:bg-slate-200 text-white dark:text-black font-semibold hover:opacity-90"
              >
                {t("cookie.save", "Save choices")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;